import { MESSAGE_CHANNELS_V3 } from "@redotech/redo-flows-common/rules-v3/types";
import { AssignV3Strategy } from "@redotech/redo-flows-common/rules/actions";
import { AssignmentTypeOption } from "@redotech/redo-flows-common/rules/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import {
  AI_NEEDS_HELP_TAG_NAME,
  AI_NO_REPLY_TAG_NAME,
} from "@redotech/redo-support-tags-common/system-tag-names";
import { Schema } from "effect";
import { makeBind } from "../field-binding";
import { shopperAgentOutreachLaneCondition } from "../shopper-agent-outreach-lane";
import { notSpamCondition } from "../spam-guard";
import { DefaultRule } from "../types";

/**
 * Shape of this rule's `configurations` JSONB blob.
 * Merchants opt-in specific channels on which the AI should auto-respond.
 */
export const SendAiResponseOnCustomerReplyConfigSchema = Schema.Struct({
  channels: Schema.Array(Schema.Literal(...MESSAGE_CHANNELS_V3)),
});

export type SendAiResponseOnCustomerReplyConfig =
  typeof SendAiResponseOnCustomerReplyConfigSchema.Type;

const bind = makeBind<SendAiResponseOnCustomerReplyConfig>();

/**
 * Default rule: Send AI response on customer reply
 *
 * When a customer sends a message on a channel the merchant has opted in,
 * and the ticket is not tagged `SPAM` or `AI_NO_REPLY`:
 * - If the AI agent is assigned, always respond.
 * - If there is no assignee, respond unless the ticket carries the
 *   `AI_NEEDS_HELP` tag (i.e. AI has already escalated to a human).
 *
 * When the channel is NOT in the allowed list and the ticket is assigned to
 * AI, the AI is unassigned (so the ticket re-enters the routing queue instead
 * of being silently left with an AI that won't respond on that channel).
 *
 * Both branches skip Shopper Agent lanes, which
 * `default-send-ai-response-on-shopper-agent-conversation` owns and answers
 * first. Without the guard on step 4 the channel opt-out would unassign the AI
 * off a lane that rule needs it on.
 *
 * Steps:
 *   1. channel + ¬agent lane + ¬spam  →  true: proceed to step 2; false: step 4
 *   2. AI assigned?     →  true: respond; false: step 3
 *   3. unassigned + ¬AI_NEEDS_HELP  →  true: respond
 *   4. (wrong channel) AI assigned + ¬agent lane?  →  true: unassign AI
 *
 * Note: this rule routes through the advancedFlow (Temporal) execution path
 * due to the branching nextFalseId. Temporal availability is required for
 * this rule to fire.
 *
 * Defaults to `enabled: false` with an empty channel list — opt-in, no-op
 * until a merchant picks channels and turns it on.
 */
export const sendAiResponseOnCustomerReplyRule = {
  id: "default-send-ai-response-on-customer-reply",
  name: "Send AI response on customer reply",
  description:
    "When a customer sends a message on an allowed channel and the ticket isn't tagged SPAM or AI_NO_REPLY, start the AI agent if AI is assigned, or if there is no assignee and the ticket isn't tagged AI_NEEDS_HELP.",
  hidden: false,
  triggerType: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,

  configSchema: SendAiResponseOnCustomerReplyConfigSchema,

  defaultConfig: {
    defaultRuleId: "default-send-ai-response-on-customer-reply",
    enabled: false,
    sortIndex: 3,
    configurations: { channels: [] },
  },

  template: {
    name: "Send AI response on customer reply",
    description:
      "When a customer sends a message on an allowed channel and the ticket isn't tagged SPAM or AI_NO_REPLY, start the AI agent if AI is assigned, or if there is no assignee and the ticket isn't tagged AI_NEEDS_HELP.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,
        nextId: "channel-spam-check",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "channel-spam-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_CHANNEL,
              channels: bind("channels", []),
              queryOperator: QueryOperator.IS,
            },
            shopperAgentOutreachLaneCondition(QueryOperator.IS_NOT),
            notSpamCondition(),
            {
              conditionType: RuleConditionType.HAS_TAG,
              tags: [AI_NO_REPLY_TAG_NAME],
              queryOperator: QueryOperator.IS_NOT,
            },
          ],
        },
        nextTrueId: "ai-assigned-check",
        nextFalseId: "wrong-channel-ai-check",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "ai-assigned-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: [AssignmentTypeOption.AI_AGENT_ASSIGNEE],
              },
              queryOperator: QueryOperator.IS,
            },
          ],
        },
        nextTrueId: "actions",
        nextFalseId: "unassigned-check",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "unassigned-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: [AssignmentTypeOption.NO_ASSIGNEE],
              },
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.HAS_TAG,
              tags: [AI_NEEDS_HELP_TAG_NAME],
              queryOperator: QueryOperator.IS_NOT,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [{ actionType: RuleActionType.START_AI_AGENT_EXECUTION }],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "wrong-channel-ai-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: [AssignmentTypeOption.AI_AGENT_ASSIGNEE],
              },
              queryOperator: QueryOperator.IS,
            },
            shopperAgentOutreachLaneCondition(QueryOperator.IS_NOT),
          ],
        },
        nextTrueId: "unassign-ai-actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "unassign-ai-actions",
        actions: [
          {
            actionType: RuleActionType.ASSIGN_V3,
            mode: { _tag: "unassign" },
            strategy: AssignV3Strategy.ROUND_ROBIN,
            maxTicketsPerUser: null,
          },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule<string, SendAiResponseOnCustomerReplyConfig>;
